import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  CloudService,
  ConfigMode,
  OrgDefaults,
  ServiceCardData,
  ServiceId,
  ServicePolicy,
} from '../../core/services/cloud.service';
import { MemberWithUser } from '../../core/services/org.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-service-settings-drawer',
  templateUrl: './service-settings-drawer.component.html',
})
export class ServiceSettingsDrawerComponent implements OnChanges {
  @Input() service: ServiceCardData | null = null;
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  draft: ServicePolicy | null = null;
  orgDefaults: OrgDefaults | null = null;
  saveState: 'idle' | 'saving' = 'idle';
  showDiscardConfirm = false;
  submitAttempted = false;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        this.resetDraft();
      } else {
        this.showDiscardConfirm = false;
        this.submitAttempted = false;
      }
    }
  }

  private resetDraft(): void {
    if (!this.service) return;
    const current = this.cloud.getServicePolicy(this.service.id as ServiceId);
    this.draft       = JSON.parse(JSON.stringify(current)) as ServicePolicy;
    this.orgDefaults = this.cloud.getOrgDefaults();
    this.saveState          = 'idle';
    this.showDiscardConfirm = false;
    this.submitAttempted    = false;
  }

  get isDirty(): boolean {
    if (!this.service || !this.draft) return false;
    const current = this.cloud.getServicePolicy(this.service.id as ServiceId);
    return JSON.stringify(current) !== JSON.stringify(this.draft);
  }

  get hasAnyEnabled(): boolean {
    if (!this.draft) return false;
    const n = this.draft.notifications;
    const t = this.draft.ticketing;
    return n.slack.enabled || n.teams.enabled || n.email.enabled ||
           t.github.enabled || t.jira.enabled;
  }

  // ── Org-default presence helpers (used by template) ─────────────────────────

  get hasOrgSlack():  boolean { return !!this.orgDefaults?.notifications.slack; }
  get hasOrgTeams():  boolean { return !!this.orgDefaults?.notifications.teams; }
  get hasOrgEmail():  boolean { return !!this.orgDefaults?.notifications.email; }
  get hasOrgGitHub(): boolean { return !!this.orgDefaults?.ticketing.github; }
  get hasOrgJira():   boolean { return !!this.orgDefaults?.ticketing.jira; }

  get orgMembers(): MemberWithUser[] { return this.cloud.getOrgMembers(); }

  // webhookUrl values from the org-defaults API are already masked; return directly.
  orgSlackSummary():  string { return this.orgDefaults?.notifications.slack?.webhookUrl ?? ''; }
  orgTeamsSummary():  string { return this.orgDefaults?.notifications.teams?.webhookUrl ?? ''; }
  orgEmailSummary():  string {
    const ids = this.orgDefaults?.notifications.email?.recipientIds ?? [];
    if (ids.length === 0) return 'No recipients configured';
    const names = ids.map(id => {
      const m = this.orgMembers.find(x => x.userId === id);
      return m ? (`${m.givenName} ${m.familyName}`.trim() || m.email) : id;
    });
    return `${ids.length} recipient${ids.length !== 1 ? 's' : ''}: ${names.join(', ')}`;
  }
  orgGitHubSummary(): string { return this.orgDefaults?.ticketing.github?.repository ?? ''; }
  orgJiraSummary():   string { return this.orgDefaults?.ticketing.jira?.projectKey   ?? ''; }

  // ── Validation error getters (shown only after first save attempt) ───────────

  get slackUrlError(): boolean {
    return this.submitAttempted &&
      (this.draft?.notifications.slack.enabled ?? false) &&
      this.draft?.notifications.slack.configMode === 'custom' &&
      !isValidUrl(this.draft?.notifications.slack.overrideConfig?.webhookUrl ?? '');
  }

  get teamsUrlError(): boolean {
    return this.submitAttempted &&
      (this.draft?.notifications.teams.enabled ?? false) &&
      this.draft?.notifications.teams.configMode === 'custom' &&
      !isValidUrl(this.draft?.notifications.teams.overrideConfig?.webhookUrl ?? '');
  }

  get emailError(): boolean {
    return this.submitAttempted &&
      (this.draft?.notifications.email.enabled ?? false) &&
      this.draft?.notifications.email.configMode === 'custom' &&
      (this.draft?.notifications.email.overrideConfig?.recipientIds?.length ?? 0) === 0;
  }

  get githubError(): boolean {
    return this.submitAttempted &&
      (this.draft?.ticketing.github.enabled ?? false) &&
      this.draft?.ticketing.github.configMode === 'custom' &&
      !(this.draft?.ticketing.github.overrideConfig?.repository?.includes('/') ?? false);
  }

  get jiraError(): boolean {
    return this.submitAttempted &&
      (this.draft?.ticketing.jira.enabled ?? false) &&
      this.draft?.ticketing.jira.configMode === 'custom' &&
      !(this.draft?.ticketing.jira.overrideConfig?.projectKey?.trim() ?? '');
  }

  // ── Incomplete-default warnings (enabled + inherited but no org default) ─────

  get slackInheritedMissing(): boolean {
    return (this.draft?.notifications.slack.enabled ?? false) &&
      this.draft?.notifications.slack.configMode === 'inherited' &&
      !this.hasOrgSlack;
  }

  get teamsInheritedMissing(): boolean {
    return (this.draft?.notifications.teams.enabled ?? false) &&
      this.draft?.notifications.teams.configMode === 'inherited' &&
      !this.hasOrgTeams;
  }

  get emailInheritedMissing(): boolean {
    return (this.draft?.notifications.email.enabled ?? false) &&
      this.draft?.notifications.email.configMode === 'inherited' &&
      !this.hasOrgEmail;
  }

  get githubInheritedMissing(): boolean {
    return (this.draft?.ticketing.github.enabled ?? false) &&
      this.draft?.ticketing.github.configMode === 'inherited' &&
      !this.hasOrgGitHub;
  }

  get jiraInheritedMissing(): boolean {
    return (this.draft?.ticketing.jira.enabled ?? false) &&
      this.draft?.ticketing.jira.configMode === 'inherited' &&
      !this.hasOrgJira;
  }

  // ── Actions ──────────────────────────────────────────────────────────────────

  setConfigMode(
    group:    'notifications' | 'ticketing',
    provider: string,
    mode:     ConfigMode,
  ): void {
    if (!this.draft) return;
    if (group === 'notifications') {
      const p = this.draft.notifications[provider as keyof typeof this.draft.notifications];
      p.configMode = mode;
    } else {
      const p = this.draft.ticketing[provider as keyof typeof this.draft.ticketing];
      p.configMode = mode;
    }
  }

  requestClose(): void {
    if (this.isDirty) {
      this.showDiscardConfirm = true;
    } else {
      this.closed.emit();
    }
  }

  confirmDiscard(): void {
    this.showDiscardConfirm = false;
    this.closed.emit();
  }

  cancelDiscard(): void {
    this.showDiscardConfirm = false;
  }

  save(): void {
    this.submitAttempted = true;
    if (!this.isFormValid() || !this.service || !this.draft) return;
    this.saveState = 'saving';
    this.cloud.saveServicePolicy(this.service.id as ServiceId, this.draft).subscribe({
      next: () => {
        this.saveState = 'idle';
        this.toast.show(`${this.service!.name} settings updated`);
        this.closed.emit();
      },
      error: () => {
        this.saveState = 'idle';
        this.toast.show('Failed to save settings', 'error');
      },
    });
  }

  private isFormValid(): boolean {
    if (!this.draft) return false;
    const n = this.draft.notifications;
    const t = this.draft.ticketing;
    if (n.slack.enabled  && n.slack.configMode  === 'custom' && !isValidUrl(n.slack.overrideConfig?.webhookUrl  ?? '')) return false;
    if (n.teams.enabled  && n.teams.configMode  === 'custom' && !isValidUrl(n.teams.overrideConfig?.webhookUrl  ?? '')) return false;
    if (n.email.enabled  && n.email.configMode  === 'custom' && (n.email.overrideConfig?.recipientIds?.length ?? 0) === 0) return false;
    if (t.github.enabled && t.github.configMode === 'custom' && !(t.github.overrideConfig?.repository?.includes('/') ?? false)) return false;
    if (t.jira.enabled   && t.jira.configMode   === 'custom' && !(t.jira.overrideConfig?.projectKey?.trim()    ?? '')) return false;
    return true;
  }
}

function isValidUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

